import type { DialogRef } from '@myosh/odin-components';
import React, { useEffect } from 'react';
import { BehaviorSubject } from 'rxjs';
import type { DynamicFormSettings } from '../../@types/forms';
import { useAppDispatch } from '../../redux/hooks';
import { useGetFormByIdQuery } from '../../redux/services/api';
import {
  type FormBuilderFormSettings,
  setFormData,
  setFormLoadingState,
  setImportedFormData,
} from '../../redux/slices/form-builder-slice';
import type { ActiveRecordContextProps } from '../active-record/active-record.component';
import FormBuilderDetails from './details/form-builder-details.component';
import FormBuilderContentArea from './form-builder-content-area.component';
import FormBuilderHeader from './form-builder-header.component';
import FormBuilderLoader from './form-builder-loader.component';
import FormBuilderSidebarMenu from './form-builder-sidebar-menu.component';

export interface FormBuilderProps {
  formId: number;
  // imported form specific props
  jobId?: string;
  moduleFormId?: number;
  templateFormId?: number;
  config?: DynamicFormSettings;

  onClose: (id: string) => void;
  panelSettings?: BehaviorSubject<ActiveRecordContextProps>;
  onCloseActiveRecordConfirmationModalRef?: DialogRef | null;
}

const FormBuilder = ({
  formId,
  jobId,
  moduleFormId,
  templateFormId,
  config,
  onClose,
  onCloseActiveRecordConfirmationModalRef,
}: FormBuilderProps) => {
  const isImportedForm = Boolean(jobId && moduleFormId && templateFormId && config);
  const dispatch = useAppDispatch();

  const { data: formQueryData, isFetching: isFormQueryDataFetching } = useGetFormByIdQuery(formId ?? 0, {
    skip: isImportedForm || formId === undefined || formId <= 0,
  });

  useEffect(() => {
    if (isImportedForm) {
      dispatch(setImportedFormData(config! as FormBuilderFormSettings));
    } else {
      dispatch(setFormLoadingState(true));
      if (!isFormQueryDataFetching && formQueryData) {
        dispatch(setFormData(formQueryData as FormBuilderFormSettings));
        dispatch(setFormLoadingState(false));
      }
    }
  }, [formQueryData, isFormQueryDataFetching, isImportedForm]);

  return (
    <>
      <FormBuilderLoader />

      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* Top Header */}
        <FormBuilderHeader
          onClose={onClose}
          formId={formId}
          jobId={jobId}
          moduleFormId={moduleFormId}
          templateFormId={templateFormId}
          onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
        />

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar */}
          <FormBuilderSidebarMenu />

          {/* Main Content Area */}
          <FormBuilderContentArea formConfig={formQueryData || config} />

          {/* Right Sidebar - Field Details */}
          <FormBuilderDetails />
        </div>
      </div>
    </>
  );
};

export default React.memo(FormBuilder);
